import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import {
    dismissPurchaseDebtBarForToday,
    isPurchaseDebtBarDismissedToday,
} from "../lib/obigPurchaseDebtDismiss";
import { roundMoneyUAH } from "../helpers/purchaseMoney";

function formatDebtForCurrency(uahValue, displayCurrency, usdSellRate) {
    const u = roundMoneyUAH(uahValue);
    if (displayCurrency === "USD") {
        const r = Number(usdSellRate);
        if (Number.isFinite(r) && r > 0) {
            const usd = Math.round((u / r) * 100) / 100;
            return {
                amountText: usd.toLocaleString("uk-UA", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }),
                unit: "$"
            };
        }
    }
    return {
        amountText: u.toLocaleString("uk-UA", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }),
        unit: "грн"
    };
}

/**
 * Борг по закупках: червона смуга, сховати до кінця дня (спільний ключ з Обігом).
 * @param {number | null} totalDebtUAH
 * @param {{ supplierId: string | null, supplierName: string, debtUAH: number }[]} [bySupplier]
 * @param {"UAH"|"USD"} [displayCurrency]
 * @param {number | null} [usdSellRate] — як у таблиці закупівель (продаж USD банком).
 * @param {() => void} [onOpenPurchasesTab] — якщо задано (сторінка Виробництво), замість посилання на /materials відкриває вкладку «Закупки».
 */
export default function ObigPurchaseDebtBar({
    totalDebtUAH,
    bySupplier = [],
    displayCurrency = "UAH",
    usdSellRate = null,
    onOpenPurchasesTab
}) {
    const [hiddenForToday, setHiddenForToday] = useState(() =>
        isPurchaseDebtBarDismissedToday()
    );

    useEffect(() => {
        setHiddenForToday(isPurchaseDebtBarDismissedToday());
    }, [totalDebtUAH]);

    const dismiss = useCallback(() => {
        dismissPurchaseDebtBarForToday();
        setHiddenForToday(true);
    }, []);

    const totalFmt = useMemo(() => {
        if (totalDebtUAH == null || totalDebtUAH <= 0) {
            return null;
        }
        return formatDebtForCurrency(totalDebtUAH, displayCurrency, usdSellRate);
    }, [totalDebtUAH, displayCurrency, usdSellRate]);

    const breakdown = useMemo(() => {
        const list = Array.isArray(bySupplier) ? bySupplier : [];
        return list
            .filter((s) => s && Number(s.debtUAH) > 0)
            .map((s, idx) => ({
                key: s.supplierId || `__${s.supplierName}__${idx}`,
                supplierName: s.supplierName || "—",
                ...formatDebtForCurrency(s.debtUAH, displayCurrency, usdSellRate)
            }));
    }, [bySupplier, displayCurrency, usdSellRate]);

    if (totalDebtUAH == null || totalDebtUAH <= 0 || hiddenForToday || !totalFmt) {
        return null;
    }

    const purchasesLink =
        typeof onOpenPurchasesTab === "function" ? (
            <button
                type="button"
                className="app-obig-purchase-debt-bar__link"
                onClick={onOpenPurchasesTab}
            >
                Закупки
            </button>
        ) : (
            <Link to="/materials/purchases" className="app-obig-purchase-debt-bar__link">
                Виробництво → Закупки
            </Link>
        );

    return (
        <div className="app-obig-purchase-debt-bar" role="alert">
            <AlertTriangle
                className="app-obig-purchase-debt-bar__icon flex-shrink-0"
                size={18}
                strokeWidth={2}
                aria-hidden
            />
            <span className="app-obig-purchase-debt-bar__label">Борг по закупках:</span>
            <div className="app-obig-purchase-debt-bar__body">
                <div className="app-obig-purchase-debt-bar__total-line">
                    <strong className="app-obig-purchase-debt-bar__amount">
                        {totalFmt.amountText}{" "}
                        {totalFmt.unit === "$" ? <span className="text-nowrap">$</span> : totalFmt.unit}
                    </strong>
                    <span className="app-obig-purchase-debt-bar__sub">
                        {" "}
                        ({purchasesLink})
                    </span>
                </div>
                {breakdown.length > 0 ? (
                    <ul className="app-obig-purchase-debt-bar__breakdown">
                        {breakdown.map((row) => (
                            <li key={row.key} className="app-obig-purchase-debt-bar__breakdown-item">
                                <span className="app-obig-purchase-debt-bar__supplier">
                                    {row.supplierName}
                                </span>
                                <span className="app-obig-purchase-debt-bar__dash text-muted"> — </span>
                                <span className="app-obig-purchase-debt-bar__supplier-sum text-nowrap">
                                    <strong>
                                        {row.amountText}{" "}
                                        {row.unit === "$" ? (
                                            <span className="text-nowrap">$</span>
                                        ) : (
                                            row.unit
                                        )}
                                    </strong>
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
            <button
                type="button"
                className="app-obig-purchase-debt-bar__close"
                onClick={dismiss}
                aria-label="Сховати до завтра"
                title="Сховати до кінця дня. Завтра нагадування з’явиться знову."
            >
                <X size={18} strokeWidth={2.25} aria-hidden />
            </button>
        </div>
    );
}
